import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AddPatientCard = ({ onAddPatient }) => {
  const { t } = useTranslation();
  const [isFlipped, setIsFlipped] = useState(false);
  const [inputErrorMessage, setInputErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    address: "",
    date_of_birth: "",
    notes: "",
  });

  const resetInputValues = () => {
    setFormData({
      name: "",
      age: "",
      address: "",
      date_of_birth: "",
      notes: "",
    });
    setInputErrorMessage("");
  };

  const handleFlip = () => {
    if (!isFlipped) {
      setIsFlipped(true);
      resetInputValues();
    }
  };

  const handleCancel = () => {
    setIsFlipped(false);
    resetInputValues();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setInputErrorMessage("");
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        setInputErrorMessage(t("Namn är obligatoriskt."));
        return;
      }

      const newPatient = {
        name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age) : null,
        address: formData.address.trim() || null,
        date_of_birth: formData.date_of_birth || null,
        notes: formData.notes.trim() || null,
        status: 'active',
      };

      await onAddPatient(newPatient);
      setIsFlipped(false);
      resetInputValues();
    } catch (error) {
      console.error("Error Details:", error);
      if (error.response) {
        setInputErrorMessage(
          error.response.data.message || error.response.data.error || t("Serverfel inträffade.")
        );
      } else if (error.request) {
        setInputErrorMessage(t("Kunde inte ansluta till servern."));
      } else {
        setInputErrorMessage(`${t("Ett oväntat fel inträffade")}: ${error.message}`);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border-2 border-dashed border-gray-300 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:scale-[1.02] group" style={{ height: '300px' }}>
      {/* Card Container with 3D flip effect */}
      <div className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
        
        {/* Front Side - Add Button */}
        <div 
          onClick={handleFlip}
          className="absolute inset-0 w-full h-full cursor-pointer backface-hidden"
        >
          <div className="relative h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mobile:w-20 mobile:h-20 tablet:w-20 tablet:h-20 laptop:w-20 laptop:h-20 desktop:w-20 desktop:h-20 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 rounded-full flex items-center justify-center mx-auto mb-3 mobile:mb-4 tablet:mb-4 laptop:mb-4 desktop:mb-4 group-hover:scale-110 transition-transform duration-300">
                <img src="/assets/icons/Plus.png" alt={t("Lägg till ny patient")} className="w-8 h-8 mobile:w-10 mobile:h-10 tablet:w-10 tablet:h-10 laptop:w-10 laptop:h-10 desktop:w-10 desktop:h-10 text-secondary-green" />
              </div>
              <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-600">{t('Lägg till ny patient')}</p>
            </div>
          </div>
        </div>

        {/* Back Side - Form */}
        <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180">
          <div className="h-full flex flex-col bg-white">
            {/* Form Header */}
            <div className="flex-shrink-0 p-2 mobile:p-3 tablet:p-3 laptop:p-3 desktop:p-3 bg-gradient-to-br from-gray-50 to-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-bold text-gray-900">
                  {t('Skapa ny patient')}
                </h3>
                <button
                  onClick={handleCancel}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <img src="/assets/icons/Dolj.png" alt={t("Stäng")} className="w-3 h-3 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Form Content */}
            <div className="flex-1 p-2 mobile:p-3 tablet:p-3 laptop:p-3 desktop:p-3 space-y-2 mobile:space-y-2 tablet:space-y-2 laptop:space-y-2 desktop:space-y-2 overflow-y-auto">
              {/* Patient Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("Namn")} *
                </label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t("Patientens namn")}
                  required
                />
              </div>

              {/* Age */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("Ålder")}
                </label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs"
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder={t("Ålder")}
                  min="0"
                  max="150"
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("Födelsedatum")}
                </label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs"
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("Adress")}
                </label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs"
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={t("Adress")}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("Anteckningar")}
                </label>
                <textarea
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs resize-none"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder={t("Anteckningar...")}
                  rows="2"
                />
              </div>

              {/* Error Message */}
              {inputErrorMessage && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-xs">{inputErrorMessage}</p>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex-shrink-0 p-2 mobile:p-3 tablet:p-3 laptop:p-3 desktop:p-3 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 bg-secondary-green text-white font-semibold rounded-lg hover:opacity-90 transition-opacity text-xs"
                  onClick={handleSubmit}
                >
                  <img
                    src="/assets/icons/Check.png"
                    alt={t("Skapa")}
                    className="w-3 h-3"
                  />
                  {t("Skapa")}
                </button>
                <button
                  className="flex-1 px-2 py-1 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-xs"
                  onClick={handleCancel}
                >
                  {t("Avbryt")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPatientCard;

